import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, Loader2, UserPlus } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import MicrosoftSignInButton from "@/components/MicrosoftSignInButton";
import { useAuth } from "@/context/AuthContext";
import OrgAutocomplete from "@/components/OrgAutocomplete";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [organization, setOrganization] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const res = await register(name, email, password, organization);
    setLoading(false);
    if (res.ok) navigate(redirect);
    else setErr(res.error);
  };

  return (
    <SiteLayout branch="main">
      <div className="bg-[var(--cream)] py-16 sm:py-24 px-4">
        <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <span className="eyebrow-dark">CREATE ACCOUNT</span>
          <h1 className="font-display text-4xl sm:text-5xl text-[var(--navy)] mt-3 leading-tight">Join Catalyst.</h1>
          <p className="text-[var(--muted)] mt-3 text-base leading-relaxed">Access memberships, purchases, and your consultation history.</p>
        </div>
        <form onSubmit={submit} className="bg-white border border-[var(--line)] p-8 sm:p-10 space-y-5 shadow-sm" data-testid="register-form" noValidate>
          <div className="space-y-3">
            <GoogleSignInButton next={redirect} label="Sign up with Google" />
            <MicrosoftSignInButton next={redirect} label="Sign up with Microsoft" />
          </div>
          <div className="relative py-2" role="separator" aria-label="Or sign up with email">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--line)]" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-4 text-[11px] tracking-[0.18em] text-[var(--muted)] font-semibold whitespace-nowrap">OR SIGN UP WITH EMAIL</span>
            </div>
          </div>
          <div>
            <label htmlFor="reg-name" className="block text-sm font-semibold text-[var(--navy)] mb-2">Full name</label>
            <input id="reg-name" required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-name"
              className="w-full px-3.5 py-3 border border-[var(--line)] bg-[#fafafa] text-[15px] text-[var(--ink)] placeholder:text-neutral-400 focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors" />
          </div>
          <div>
            <label htmlFor="reg-org" className="block text-sm font-semibold text-[var(--navy)] mb-2">
              Organization <span className="text-[var(--muted)] font-normal text-xs">(optional)</span>
            </label>
            <OrgAutocomplete value={organization} onChange={setOrganization} testId="reg-org" />
          </div>
          <div>
            <label htmlFor="reg-email" className="block text-sm font-semibold text-[var(--navy)] mb-2">Email address</label>
            <input id="reg-email" required type="email" autoComplete="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reg-email"
              className="w-full px-3.5 py-3 border border-[var(--line)] bg-[#fafafa] text-[15px] text-[var(--ink)] placeholder:text-neutral-400 focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors" />
          </div>
          <div>
            <label htmlFor="reg-password" className="block text-sm font-semibold text-[var(--navy)] mb-2">Password</label>
            <div className="relative">
              <input id="reg-password" required minLength={6} type={showPw ? "text" : "password"} autoComplete="new-password" placeholder="At least 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reg-password"
                className="w-full pl-3.5 pr-11 py-3 border border-[var(--line)] bg-[#fafafa] text-[15px] text-[var(--ink)] placeholder:text-neutral-400 focus:bg-white focus:border-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 transition-colors" />
              <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--navy)] p-1"
                data-testid="reg-toggle-password" aria-label={showPw ? "Hide password" : "Show password"}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-[var(--muted)] mt-1.5">Minimum 6 characters. Longer & mixed characters recommended.</p>
          </div>
          {err && (
            <div role="alert" className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-800 text-sm p-3.5" data-testid="register-error">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span className="leading-relaxed">{err}</span>
            </div>
          )}
          <button type="submit" disabled={loading} className="btn btn-navy w-full justify-center h-12 text-[15px] disabled:opacity-60 disabled:cursor-not-allowed" data-testid="reg-submit">
            {loading ? (<><Loader2 className="animate-spin mr-2" size={16} /> Creating account…</>) : (<><UserPlus size={16} className="mr-2" /> Create account</>)}
          </button>
        </form>
        <p className="text-center text-sm text-[var(--muted)] mt-6">
          Already registered?{" "}
          <Link to="/login" className="text-[var(--navy)] font-bold hover:text-[var(--gold)] underline underline-offset-2">Sign in</Link>
        </p>
        </div>
      </div>
    </SiteLayout>
  );
}
